<!--
@component
SchedulePageMatchList

See markup below.
-->

<script lang="ts">
  import MatchStrip from '$lib/components/common/MatchStrip/index.svelte';
  import MatchStripPlaceholder from '$lib/components/common/MatchStrip/Placeholder.svelte';
  import LazyMount from '$lib/components/common/LazyMount/index.svelte';
  import { appState } from '$lib/stores/index.svelte';
  import { fly } from 'svelte/transition';
  import type { ScheduleItem } from './types';

  // Cap per-match stagger so very long lists don't end up with a huge tail.
  const MATCH_DELAY_STEP_MS = 50;
  const MATCH_DELAY_MAX_MS = 500;

  // Approximate height of a single rendered MatchStrip card (used as the
  // placeholder size for off-screen rows so the scrollbar stays accurate).
  const MATCH_CARD_HEIGHT = 'calc(3.75 * var(--theme-font-size-base))';

  let { item }: { item: ScheduleItem } = $props();
</script>

<!--
  Lightweight shimmer that mirrors `MatchStrip`'s exact footprint (flag +
  team code + middle slot + team code + flag). Rendered inside
  `LazyMount`'s reserved space until the row scrolls near the viewport, so
  no layout shift occurs when the live strip mounts. `label={null}`
  suppresses the per-row live-region since the parent `<ol aria-label>`
  already labels the list as a whole.
-->
{#snippet placeholder()}
  <MatchStripPlaceholder label={null} />
{/snippet}

<!--
  List for a single date. Off-screen match rows are lazy-mounted via
  `LazyMount`, so their `MatchStrip` (with hover effects, live-card shadows,
  flag images, etc.) doesn't hydrate or paint until the row scrolls near the
  viewport. See #57.
-->
<li class="column mx-auto fmb-7" in:fly|global={{ y: -5, duration: 500 }}>
  <div class="date">
    <div class="hr"></div>
    <h2 class="name h4 mt-0 text-center">
      {item.localDateStamp.month}
      {#if appState.isLoading}
        &hellip;
      {:else}
        {item.localDateStamp.day}
      {/if}
    </h2>
    <div class="hr"></div>
  </div>

  <!-- One sub-list per stage / group on this date -->
  {#each item.matchesOnDate as { stage, matchesForStage } (stage)}
    <h3 class="body-caption">{stage}</h3>
    <ol
      class="mx-auto"
      aria-label={`${stage} matches on ${item.localDateStamp.month} ${item.localDateStamp.day}`}
    >
      {#each matchesForStage as match, i (match.matchInfo.id)}
        <li
          class="fmy-2"
          in:fly|global={{
            y: -15,
            duration: 500,
            delay: Math.min((i + 1) * MATCH_DELAY_STEP_MS, MATCH_DELAY_MAX_MS),
          }}
        >
          <LazyMount height={MATCH_CARD_HEIGHT} {placeholder}>
            <MatchStrip link={true} {match} />
          </LazyMount>
        </li>
      {/each}
    </ol>
  {/each}
</li>

<style lang="scss">
  @use '../../styles/mixins' as mixins;

  ol,
  li {
    list-style: none;
    padding: 0;
    --width: calc(22 * var(--theme-font-size-base));
    max-width: 100%;
  }

  .date {
    @include mixins.match-stage;
  }

  .column {
    width: var(--width);
    max-width: 100%;
  }
</style>
